import {
  TarefaDTO,
  TarefaNotificacoesDTO,
  TarefaMensagemDTO,
  TarefaVisualizacaoDTO,
  TarefaObservadorDTO,
  TarefaQuestionarArquivoAprovacaoPayload,
  TarefaQuestionarBriefingPayload,
  TarefaQuestionarJobPayload,
  TarefaQuestionarPaperPayload,
  TarefaQuestionarVersaoPayload,
  TarefaMaisAtrasadaDTO,
  TarefaRankingResponsavelDTO,
  TarefaRankingModo,
  TarefaResumoAdminDTO,
  TarefaResumoAdminPeriodoDTO,
  TarefaResumoPeriodoDTO,
  TarefaResumoTotalDTO,
  SituacaoTarefasDTO,
  TarefaSolicitarAjusteApPayload,
  TarefaCobrarAprovacaoApPayload,
  TarefaSolicitarDesfaturamentoPayload,
  TarefaSolicitarAjustePayload,
  TarefaSolicitarApPayload,
  TarefaSolicitarOrcamentoPreProducaoPayload,
  TarefasPendentesAtribuidasCriteria,
  CriarTarefaGenericaPayload,
  EditarTarefaGenericaPayload,
  CriarArquivoJobPayload,
} from "@/types/tarefa.type";
import { DadosConclusaoDTO } from "@/types/conclusao.type";
import { buildQueryParams } from "@/utils/build-query-params";
import { httpClient } from "./httpClient";
import { PageResponse } from "@/types/http.type";
import {
  TarefaAtribuidoFiltrosCriteria,
  TarefaAtribuidoFiltrosOptions,
  TarefaCriadorFiltrosCriteria,
  TarefaCriadorFiltrosOptions,
  TarefaFiltrosCriteria,
  TarefaFiltrosOptions,
  TarefaObservadaFiltrosCriteria,
  TarefaObservadaFiltrosOptions,
  TarefaTipo,
} from "@workflow/types";
import { ArquivoDTO } from "@/types/arquivo.type";
import { TarefaLogDTO } from "@/types/tarefa-log.type";

class TarefaService {
  private readonly basePath = "/api/v1/tarefas";

  public async getTarefaById(id: number) {
    return await httpClient.get<TarefaDTO>(`${this.basePath}/${id}`);
  }

  public async countPendentes() {
    return await httpClient.get<number>(`${this.basePath}/count-pendentes`);
  }

  public async getMensagensByTarefaId(id: number) {
    return await httpClient.get<TarefaMensagemDTO[]>(`${this.basePath}/${id}/mensagens`);
  }

  public async getArquivosByTarefaId(id: number) {
    return await httpClient.get<ArquivoDTO[]>(`${this.basePath}/${id}/arquivos`);
  }

  public async downloadArquivo(tarefaId: number, arquivoId: number) {
    return await httpClient.get<string>(
      `${this.basePath}/${tarefaId}/anexos/${arquivoId}/download`,
    );
  }

  public async getObservadoresByTarefaId(id: number) {
    return await httpClient.get<TarefaObservadorDTO[]>(`${this.basePath}/${id}/observadores`);
  }

  public async getResumoTotalTarefas() {
    return await httpClient.get<TarefaResumoTotalDTO>(`${this.basePath}/resumo-totais`);
  }

  public async getResumoPeriodoTarefas(dias: number) {
    return await httpClient.get<TarefaResumoPeriodoDTO>(
      `${this.basePath}/resumo-periodo?dias=${dias}`,
    );
  }

  public async getResumoAdminTarefas() {
    return await httpClient.get<TarefaResumoAdminDTO>(`${this.basePath}/resumo-admin`);
  }

  public async getResumoAdminPorPeriodoTarefas(dias: number) {
    return await httpClient.get<TarefaResumoAdminPeriodoDTO>(
      `${this.basePath}/resumo-admin/periodo?dias=${dias}`,
    );
  }

  public async getRankingResponsaveisTarefas(
    modo: TarefaRankingModo = "abertas",
    limit = 10,
    dias?: number,
  ) {
    return await httpClient.get<TarefaRankingResponsavelDTO[]>(
      `${this.basePath}/resumo-admin/ranking-responsaveis?modo=${modo}&limit=${limit}${dias ? `&dias=${dias}` : ""}`,
    );
  }

  public async getTarefasMaisAtrasadas(limit = 10, dias?: number) {
    return await httpClient.get<TarefaMaisAtrasadaDTO[]>(
      `${this.basePath}/resumo-admin/mais-atrasadas?limit=${limit}${dias ? `&dias=${dias}` : ""}`,
    );
  }

  public async getMinhaSituacao() {
    return await httpClient.get<SituacaoTarefasDTO>(`${this.basePath}/minha-situacao`);
  }

  public async getPreviewTarefa(tipo: TarefaTipo, payload: any) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/preview`, { tipo, payload });
  }

  public async getFiltrosTarefa() {
    return await httpClient.get<TarefaFiltrosOptions>(`${this.basePath}/filtros`);
  }

  public async getFiltrosTarefaAtribuidasAMim() {
    return await httpClient.get<TarefaAtribuidoFiltrosOptions>(
      `${this.basePath}/filtros-atribuidos`,
    );
  }

  public async getFiltrosTarefaCriadasPorMim() {
    return await httpClient.get<TarefaCriadorFiltrosOptions>(`${this.basePath}/filtros-criados`);
  }

  public async getFiltrosTarefaObservadasPorMim() {
    return await httpClient.get<TarefaObservadaFiltrosOptions>(
      `${this.basePath}/filtros-observadas`,
    );
  }

  public async listTarefas(filter?: TarefaFiltrosCriteria) {
    let url = `${this.basePath}`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<TarefaDTO>>(url);
  }

  public async listTarefasCriadasPorMim(filter?: TarefaCriadorFiltrosCriteria) {
    let url = `${this.basePath}/criadas-por-mim`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<TarefaDTO>>(url);
  }

  public async listTarefasAtribuidasAMim(filter?: TarefaAtribuidoFiltrosCriteria) {
    let url = `${this.basePath}/atribuidas-a-mim`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<TarefaDTO>>(url);
  }

  public async listTarefasObservadasPorMim(filter?: TarefaObservadaFiltrosCriteria) {
    let url = `${this.basePath}/observadas-por-mim`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<TarefaDTO>>(url);
  }

  /** Notificações do usuário logado: aba "Pendentes" (por status) e aba "Mensagens" (não lidas), já separadas. */
  public async getNotificacoes(quantity?: number) {
    const url = `${this.basePath}/notificacoes`;
    return await httpClient.get<TarefaNotificacoesDTO>(url, { params: { quantity } });
  }

  /** Tarefas públicas dos jobs a que o usuário tem acesso (página "Públicos"), server-driven. */
  public async listTarefasPublicas(filter?: TarefaFiltrosCriteria) {
    let url = `${this.basePath}/publicas`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<TarefaDTO>>(url);
  }

  /** Todas as tarefas de um job (qualquer status), paginadas/server-driven e filtradas por visibilidade. */
  public async listTarefasPorJobPaginado(jobId: number, filter?: TarefaFiltrosCriteria) {
    let url = `${this.basePath}/job/${jobId}/paginado`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<TarefaDTO>>(url);
  }

  public async listPendentesAtribuidasAMim(criteria: TarefasPendentesAtribuidasCriteria = {}) {
    let url = `${this.basePath}/pendentes/atribuidas-a-mim`;

    const query = buildQueryParams(criteria);
    if (query) url += "?" + query;

    return await httpClient.get<TarefaDTO[]>(url);
  }

  public async createSolicitarAp(payload: TarefaSolicitarApPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/solicitar-ap`, payload);
  }

  public async createSolicitarOrcamentoPreProducao(
    payload: TarefaSolicitarOrcamentoPreProducaoPayload,
  ) {
    return await httpClient.post<TarefaDTO>(
      `${this.basePath}/solicitar-orcamento-pre-producao`,
      payload,
    );
  }

  public async createSolicitarOrcamentoComVersaoPendente(
    payload: TarefaSolicitarOrcamentoPreProducaoPayload,
  ) {
    return await httpClient.post<TarefaDTO>(
      `${this.basePath}/solicitar-orcamento-com-versao-pendente`,
      payload,
    );
  }

  public async questionarVersao(payload: TarefaQuestionarVersaoPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/questionar-versao`, payload);
  }

  public async questionarJob(payload: TarefaQuestionarJobPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/questionar-job`, payload);
  }

  public async questionarBriefing(payload: TarefaQuestionarBriefingPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/questionar-briefing`, payload);
  }

  public async questionarPaper(payload: TarefaQuestionarPaperPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/questionar-paper`, payload);
  }

  public async questionarArquivoAprovacao(payload: TarefaQuestionarArquivoAprovacaoPayload) {
    return await httpClient.post<TarefaDTO>(
      `${this.basePath}/questionar-arquivo-aprovacao`,
      payload,
    );
  }

  public async solicitarAjustePlanilhaAtendimento(payload: TarefaSolicitarAjustePayload) {
    return await httpClient.post<TarefaDTO>(
      `${this.basePath}/solicitar-ajuste-planilha-atendimento`,
      payload,
    );
  }

  public async createSolicitarAjusteAp(payload: TarefaSolicitarAjusteApPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/solicitar-ajuste-ap`, payload);
  }

  public async createCobrarAprovacaoAp(payload: TarefaCobrarAprovacaoApPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/cobrar-aprovacao-ap`, payload);
  }

  public async createSolicitarDesfaturamento(payload: TarefaSolicitarDesfaturamentoPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/solicitar-desfaturamento`, payload);
  }

  public async concluir(id: number, dadosConclusao?: DadosConclusaoDTO) {
    return await httpClient.post<TarefaDTO>(
      `${this.basePath}/${id}/concluir`,
      dadosConclusao ? { dadosConclusao } : undefined,
    );
  }

  public async criarGenerica(payload: CriarTarefaGenericaPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}`, payload);
  }

  public async editarGenerica(id: number, payload: EditarTarefaGenericaPayload) {
    return await httpClient.put<TarefaDTO>(`${this.basePath}/${id}/generica`, payload);
  }

  public async criarBriefing(payload: CriarArquivoJobPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/criar-briefing`, payload);
  }

  public async criarPaper(payload: CriarArquivoJobPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/criar-paper`, payload);
  }

  public async criarEstudoVerba(payload: CriarArquivoJobPayload) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/criar-estudo-verba`, payload);
  }

  public async cancelar(id: number) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/${id}/cancelar`);
  }

  public async emExecucao(id: number) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/${id}/em-execucao`);
  }

  public async arquivar(id: number) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/${id}/arquivar`);
  }

  public async reabrir(id: number) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/${id}/reabrir`);
  }

  public async reatribuir(id: number, atribuidoAId: number) {
    return await httpClient.put<TarefaDTO>(`${this.basePath}/${id}/reatribuir`, { atribuidoAId });
  }

  public async alterarDataPrazo(id: number, dataPrazo: Date | null) {
    return await httpClient.put<TarefaDTO>(`${this.basePath}/${id}/alterar-data-prazo`, {
      dataPrazo,
    });
  }

  public async adicionarMensagem(id: number, mensagem: string) {
    return await httpClient.post<TarefaMensagemDTO>(`${this.basePath}/${id}/mensagens`, {
      mensagem,
    });
  }

  /**
   * Marca a conversa como visualizada agora — zera o "não lida" desta tarefa para
   * o usuário logado. Retorna a visualização ANTERIOR (ou null), usada pra
   * destacar no chat as mensagens que chegaram desde a última visita.
   */
  public async marcarVisualizada(id: number) {
    return await httpClient.post<{ visualizadoAnteriorEm: string | null }>(
      `${this.basePath}/${id}/visualizar`,
      {},
    );
  }

  /** Quem já viu a conversa da tarefa (criador/atribuído/observadores) e quando. */
  public async getVisualizacoesByTarefaId(id: number) {
    return await httpClient.get<TarefaVisualizacaoDTO[]>(`${this.basePath}/${id}/visualizacoes`);
  }

  public async anexarArquivo(tarefaId: number, arquivoId: number) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/${tarefaId}/anexar-arquivo`, {
      arquivoId,
    });
  }

  public async removerArquivo(tarefaId: number, arquivoId: number) {
    return await httpClient.delete<TarefaDTO>(`${this.basePath}/${tarefaId}/anexos/${arquivoId}`);
  }

  public async adicionarObservador(tarefaId: number, pessoasIds: number[]) {
    return await httpClient.post<TarefaDTO>(`${this.basePath}/${tarefaId}/observadores`, {
      pessoasIds,
    });
  }

  public async removerObservador(tarefaId: number, observadorId: number) {
    return await httpClient.delete<TarefaDTO>(
      `${this.basePath}/${tarefaId}/observadores/${observadorId}`,
    );
  }

  /** Histórico de eventos de negócio da tarefa (criação, transições, prazo, anexo, observador, mensagem). */
  public async getHistoricosByTarefaId(id: number) {
    return await httpClient.get<PageResponse<TarefaLogDTO>>(`${this.basePath}/${id}/logs`, {
      params: { page: 1, limit: 200, sort: "tarefaLog.criadoEm", order: "DESC" },
    });
  }
}

export const tarefaService = new TarefaService();
